import React, { useContext, useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
  zIndexLayers,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Svg, Button } from './ui';
import CartItem from './CartItem';
import CartCrossSellProduct from './CartCrossSellProduct';
import { formatPrice, discountPrice } from '../utils';
import { StoreContext } from '../context/StoreContext';
import basketIcon from '../images/basket-icon.inline.svg';
import deliveryIcon from '../images/delivery-icon.inline.svg';

const StyledCart = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  padding: 0 20px;
  width: 100%;
  max-width: 450px;
  background-color: ${standardColours.white};
  z-index: ${zIndexLayers.fifth};
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: ${standardTransition('transform')};

  ${minBreakpointQuery.smedium`
    padding-left: 30px;
    padding-right: 30px;
    max-width: 470px;
  `}

  ${minBreakpointQuery.large`
    padding-right: 35px;
    padding-left: 35px;
    max-width: 480px;
  `}
`;

const StyledHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 22px 0;
  border-bottom: 1px solid ${standardColours.transparentBlack(0.07)};

  ${minBreakpointQuery.large`
    padding-top: 28px;
    padding-bottom: 28px;
  `}
`;

const StyledHeading = styled.h3`
  display: flex;
  align-items: center;
  color: ${standardColours.black};
  font-weight: ${fontWeights.regular};
  ${fontSize(16)};

  ${minBreakpointQuery.large`
    ${fontSize(18)};
  `}

  svg {
    margin-right: 26px;
    height: 30px;
    width: 30px;
  }

  strong {
    margin-right: 6px;
  }
`;

const StyledClose = styled.button`
  background: none;
  border: none;

  &:before {
    content: '✕';
    color: ${standardColours.black};
    ${fontSize(22)};
    line-height: 1;
  }

  span {
    ${visuallyHidden()};
  }
`;

const StyledItems = styled.ul`
  display: grid;
  gap: 25px;
`;

const StyledItem = styled.li``;

const StyledTotals = styled.div`
  margin-top: 26px;
`;

const StyledText = styled.p`
  margin: 4px 0;
  color: ${standardColours.black};
  ${fontSize(16)};
  text-align: center;

  ${minBreakpointQuery.large`
    ${fontSize(18)};
  `}
`;

const DeliveryMessage = styled.p`
  display: flex;
  align-items: center;
  margin-top: 26px;
  padding: 14px 18px;
  color: ${brandColours.septenary};
  ${fontSize(15)};
  text-align: center;
  background-color: ${brandColours.secondary};

  svg {
    margin-right: 14px;
    height: 32px;
    width: 32px;
  }
`;

const StyledButton = styled(Button)`
  display: block;
  margin: 28px auto;
  padding-top: 12px;
  padding-bottom: 12px;
  width: 100%;
  max-width: 340px;
`;

const StyledCrossSellProducts = styled.div`
  margin: 28px 0;
`;

const StyledSubheading = styled.h4`
  margin-bottom: 22px;
  color: ${brandColours.tertiary};
  ${fontSize(16)};
  text-align: center;
`;

const Cart = ({ currentProductId, crossSellProducts }) => {
  const {
    allShopifyProductVariant,
    datoCmsDelivery: { freeDeliveryAmount },
  } = useStaticQuery(graphql`
    query CartQuery {
      allShopifyProductVariant {
        nodes {
          shopifyId
          quantityDiscounts: metafield(
            key: "quantity_discounts"
            namespace: "my_fields"
          ) {
            value
          }
          product {
            quantityDiscounts: metafield(
              key: "quantity_discounts"
              namespace: "my_fields"
            ) {
              value
            }
            isRetailProduct: metafield(
              key: "retail_product"
              namespace: "my_fields"
            ) {
              value
            }
          }
        }
      }
      datoCmsDelivery {
        freeDeliveryAmount
      }
    }
  `);

  const { isCartOpen, toggleCartOpen, checkout } = useContext(StoreContext);

  const { lineItems: initialLineItems, webUrl } = checkout;

  const lineItems = initialLineItems.map(lineItem => {
    const productVariant = allShopifyProductVariant.nodes.find(
      ({ shopifyId }) => shopifyId === lineItem.variant.id
    );

    let lineItemDiscount = 0;

    const quantityDiscounts = productVariant.quantityDiscounts
      ? JSON.parse(productVariant.quantityDiscounts.value)
      : productVariant.product.quantityDiscounts
      ? JSON.parse(productVariant.product.quantityDiscounts.value)
      : [];

    quantityDiscounts.forEach(quantityDiscount => {
      const [quantity, discount] = quantityDiscount.split('|');

      if (lineItem.quantity >= quantity) {
        lineItemDiscount = discount;
      }
    });

    return {
      ...lineItem,
      variant: {
        ...lineItem.variant,
        priceV2: {
          amount: discountPrice(
            lineItem.variant.priceV2.amount,
            lineItemDiscount
          ),
        },
        quantityDiscounts: quantityDiscounts,
        discount: parseInt(lineItemDiscount),
      },
      isRetailProduct:
        productVariant.product.isRetailProduct &&
        JSON.parse(productVariant.product.isRetailProduct.value),
    };
  });

  const subtotalPrice = lineItems.reduce(
    (
      subtotal,
      {
        quantity,
        variant: {
          priceV2: { amount },
        },
      }
    ) => subtotal + quantity * amount,
    0
  );

  const totalPrice = lineItems.reduce(
    (
      subtotal,
      {
        quantity,
        variant: {
          priceV2: { amount },
        },
        isRetailProduct,
      }
    ) => subtotal + quantity * (isRetailProduct ? amount : amount * 1.2),
    0
  );

  const vatAmount = totalPrice - subtotalPrice;

  const filteredCrossSellProducts =
    crossSellProducts &&
    lineItems.find(({ variant }) => variant.product.id === currentProductId)
      ? crossSellProducts.filter(
          ({ shopifyId }) =>
            !lineItems.find(({ variant }) => variant.product.id === shopifyId)
        )
      : [];

  const [checkoutUrl, setCheckoutUrl] = useState();

  useEffect(
    () =>
      setCheckoutUrl(
        webUrl.replace(
          `${process.env.GATSBY_SHOPIFY_SHOP_NAME}.myshopify.com`,
          process.env.GATSBY_CUSTOM_SHOPIFY_STORE_URL
        )
      ),
    [webUrl]
  );

  return (
    <StyledCart isOpen={isCartOpen}>
      <StyledHeader>
        <StyledHeading>
          <Svg image={basketIcon} />
          <strong>{lineItems.length}</strong> item
          {lineItems.length > 1 && 's'} in your cart
        </StyledHeading>
        <StyledClose title="Close" onClick={toggleCartOpen}>
          <span>Close</span>
        </StyledClose>
      </StyledHeader>
      {lineItems.length > 0 ? (
        <>
          <StyledItems>
            {lineItems.map(lineItem => (
              <StyledItem key={lineItem.id}>
                <CartItem product={lineItem} />
              </StyledItem>
            ))}
          </StyledItems>
          <StyledTotals>
            {vatAmount > 0 && (
              <>
                <StyledText>
                  Subtotal: <strong>{formatPrice(subtotalPrice)}</strong>
                </StyledText>
                <StyledText>
                  VAT: <strong>{formatPrice(vatAmount)}</strong>
                </StyledText>
              </>
            )}
            <StyledText>
              Total: <strong>{formatPrice(totalPrice)}</strong>
            </StyledText>
          </StyledTotals>
          {freeDeliveryAmount && (
            <DeliveryMessage>
              <Svg image={deliveryIcon} />
              {freeDeliveryAmount - subtotalPrice > 0
                ? `Spend ${formatPrice(
                    freeDeliveryAmount - subtotalPrice
                  )} more to
                qualify for free shipping`
                : `Great, you've qualified for free shipping`}
            </DeliveryMessage>
          )}
          <StyledButton
            alt={true}
            onClick={() => {
              // See if any line items meet discount rules

              lineItems.some(lineItem => lineItem.variant.discount > 0)
                ? fetch('/.netlify/functions/shopify-order', {
                    method: 'POST',
                    body: JSON.stringify({
                      lineItems: lineItems,
                    }),
                  })
                    .then(response => response.json())
                    .then(data => {
                      window.location.href = data.checkoutUrl;
                    })
                    .catch(e => console.log(e))
                : (window.location.href = checkoutUrl);
            }}
          >
            Proceed to Secure Checkout
          </StyledButton>
          {filteredCrossSellProducts.length > 0 && (
            <StyledCrossSellProducts>
              <StyledSubheading>
                Customers who purchased this item also loved
              </StyledSubheading>
              <StyledItems>
                {filteredCrossSellProducts.map((crossSellProduct, id) => (
                  <StyledItem key={id}>
                    <CartCrossSellProduct {...crossSellProduct} />
                  </StyledItem>
                ))}
              </StyledItems>
            </StyledCrossSellProducts>
          )}
        </>
      ) : (
        <>
          <StyledText>Your Basket is Empty</StyledText>
          <StyledButton onClick={toggleCartOpen}>
            Continue Shopping
          </StyledButton>
        </>
      )}
    </StyledCart>
  );
};

export default Cart;
